import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  URL_HOME,
  URL_NAME_PUBLISH,
  URL_NAME_REJECT,
  URL_VIDEODETAIL,
} from "../../../../app/src/Components/Routes/Location";
import { turnOffLoadingActionCreator } from "../../../../app/src/actions/commonActions";
import "./PublishRejectStyles.css";
import { getAnonContentsService, getContentsService, getUserDataService, publishContentService, searchUserService, sendMailService } from "../../../../app/src/services/services";
import WithAuthorization from "../../../../app/src/Utils/WithAuthorization";
import { isCordovaAvailable } from "../../../../app/src/Utils/secureStorageUtil";
import Spinner from "../../../../detail/src/Pages/DetailPage/Loader";
import { MULTITENANT_CONSTANTS, TENANT } from "../../../../app/src/Resources/Multitenant/tenantConstants";

const PublishRejectComponent = ({ event }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { keycloak, initialized } = useKeycloak();
  const { contentId} = useParams();
  const query = new URLSearchParams(window.location.search);
  const payload = query.get("payload");
  const [publishRejectLoading, setPublishRejectLoading] = useState(true);
  const [publishRejectContentError, setPublishRejectContentError] = useState(false);
  const [isRejectConfirmationStep, setIsRejectConfirmationStep] = useState(event === URL_NAME_REJECT);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectReasonError, setRejectReasonError] = useState(false);
  
  const publishRejectObject = {
    [URL_NAME_PUBLISH]: {
      success: {
        title: t("user.publish.title"),
        text: t("user.publish.text"),
      },
      error: {
        title: t("user.publish.error.title"),
        text: t("user.publish.error.text"),
      }
    },
    [URL_NAME_REJECT]: {
      success: {
        title: t("user.reject.title"),
        text: t("user.reject.text"),
      },
      error: {
        title: t("user.reject.error.title"),
        text: t("user.reject.error.text"),
      }
    },
  };

  const turnOffLoading = () => {
    dispatch(turnOffLoadingActionCreator());
  };

  const getCssVarValue = (variableName) => {
    if (typeof window === "undefined" || !document?.documentElement) return "";
    const value = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue(variableName);
    return (value || "").trim();
  };

  const colorToRgba = (color, alpha) => {
    const c = (color || "").trim();
    if (!c) return "";
    if (c.startsWith("rgba(")) return c;
    if (c.startsWith("rgb(")) {
      const match = c.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
      if (!match) return c;
      const [, r, g, b] = match;
      return `rgba(${r},${g},${b},${alpha})`;
    }
    if (c.startsWith("#")) {
      const hex = c.slice(1);
      const normalized =
        hex.length === 3
          ? hex
              .split("")
              .map((ch) => ch + ch)
              .join("")
          : hex;
      if (normalized.length !== 6) return c;
      const r = parseInt(normalized.slice(0, 2), 16);
      const g = parseInt(normalized.slice(2, 4), 16);
      const b = parseInt(normalized.slice(4, 6), 16);
      if ([r, g, b].some((n) => Number.isNaN(n))) return c;
      return `rgba(${r},${g},${b},${alpha})`;
    }
    return c;
  };

  const sendAuthorNotificationMailWithData = (isPublish, mailData, rejectReason = "", successCallback = () => {}, errorCallback = () => {}) => {
    const mailBody = {
      to: mailData.email,
      subject: isPublish
        ? t("user.publish.mail.subject", { title: mailData.title })
        : t("user.reject.mail.subject", { title: mailData.title }),
      plaintext: isPublish
        ? t("user.publish.mail.plaintext", { title: mailData.title, url: mailData.contentUrl })
        : t("user.reject.mail.plaintext", { title: mailData.title, reason: rejectReason }),
      htmlbody: isPublish
        ? t("user.publish.mail.htmlbody", {
            title: mailData.title,
            url: mailData.contentUrl,
            ...mailData.emailTheme,
          })
        : t("user.reject.mail.htmlbody", { title: mailData.title, reason: rejectReason, ...mailData.emailTheme }),
      from:'noreply@cinfo.es',
      display: `${MULTITENANT_CONSTANTS[TENANT].pageTitle} - ${t("global.system")}`
    };

    sendMailService(mailBody, keycloak?.token, successCallback, errorCallback);
  };

  const getAuthorNotificationData = (successCallback = () => {}, errorCallback = () => {}) => {
    getContentsService(TENANT, contentId, keycloak?.token, null, (contentData) => {
      getUserDataService(
        TENANT,
        contentData?.data?.author,
        keycloak?.token,
        (userData) => {
          const title = contentData?.data?.title || "";
          const contentUrl = `${window.location.origin}${URL_VIDEODETAIL}/${contentId}`;

          const emailTheme = {
            outerBackgroundColor: getCssVarValue("--backgroundColor"),
            panelBackgroundColor: getCssVarValue("--appColor"),
            panelTextColor: getCssVarValue("--defaultButtonTextColor"),
            contentBoxBackgroundColor: colorToRgba(
              getCssVarValue("--defaultButtonTextColor"),
              0.1,
            ),
            linkBackgroundColor: getCssVarValue("--defaultButtonTextColor"),
            linkTextColor: getCssVarValue("--appColor"),
          };

          successCallback({
            email: userData.email,
            title,
            contentUrl,
            emailTheme,
          });
        },
        errorCallback,
      );
    });
  };

  const sendAuthorNotificationMail = (isPublish, successCallback = () => {}, errorCallback = () => {}) => {
    getAuthorNotificationData(
      (mailData) => sendAuthorNotificationMailWithData(isPublish, mailData, "", successCallback, errorCallback),
      errorCallback,
    );
  };

  const publishContentCallback = (data) => {
    if (data?.error_error?.http_status_code == "500") {
      setPublishRejectContentError(true);
      setPublishRejectLoading(false);  
    } else {
      setPublishRejectContentError(false);
      setPublishRejectLoading(false);

      if (event === URL_NAME_PUBLISH) {
        sendAuthorNotificationMail(true, () => {}, () => {});
      }
    }
  };

  const publishContentErrorCallback = (error) => {
    setPublishRejectContentError(true);
    setPublishRejectLoading(false);
  };

  const handleRejectReasonChange = (event) => {
    setRejectReason(event.target.value);
    if (event.target.value.trim()) {
      setRejectReasonError(false);
    }
  };

  const handleConfirmReject = () => {
    const normalizedRejectReason = rejectReason.trim();

    if (!normalizedRejectReason) {
      setRejectReasonError(true);
      return;
    }

    setRejectReasonError(false);
    setIsRejectConfirmationStep(false);
    setPublishRejectLoading(true);
    setPublishRejectContentError(false);

    getAuthorNotificationData(
      (mailData) => {
        publishContentService(
          payload,
          keycloak?.token,
          (data) => {
            if (data?.error_error?.http_status_code == "500") {
              setPublishRejectContentError(true);
              setPublishRejectLoading(false);
            } else {
              setPublishRejectContentError(false);
              setPublishRejectLoading(false);
              sendAuthorNotificationMailWithData(false, mailData, normalizedRejectReason, () => {}, () => {});
            }
          },
          publishContentErrorCallback,
          normalizedRejectReason,
        );
      },
      publishContentErrorCallback,
    );
  };

  useEffect(() => {
    turnOffLoading();
    setPublishRejectLoading(true);
    setPublishRejectContentError(false);
    setRejectReason("");
    setRejectReasonError(false);
    if ((!!keycloak && !!initialized) || isCordovaAvailable()) {
      if (!keycloak.authenticated) {
        keycloak.login({ locale: MULTITENANT_CONSTANTS[TENANT]?.defaultLocale });
      }
      if (event === URL_NAME_REJECT) {
        setIsRejectConfirmationStep(true);
        setPublishRejectLoading(false);
      } else {
        setIsRejectConfirmationStep(false);
        publishContentService(payload, keycloak?.token, publishContentCallback, publishContentErrorCallback);
      }
    } else {
      setIsRejectConfirmationStep(false);
      setPublishRejectContentError(true);
      setPublishRejectLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  const handleRedirectToHome = () => {
    history.push(URL_HOME);
  };

  const handleRedirectToPlayer = () => {
    history.push(`${URL_VIDEODETAIL}/${contentId}`);
  };

  const viewState = publishRejectContentError ? "error" : "success";

  return (
    <div className={"publishReject"}>
      <div className={"logoApp"}>
        <div className={"tenantLogo"} aria-label={"tenant-logo"} />
      </div>
      <div className={"content"}>
        <div className={"text"}>
          {publishRejectLoading ? (
            <>
              <Spinner />
            </>
          ) : isRejectConfirmationStep && event === URL_NAME_REJECT ? (
            <>
              <h1>{t("user.reject.confirm.title")}</h1>
              <p>{t("user.reject.confirm.description")}</p>
              <div className="reject-confirmation-form">
                <textarea
                  className="reject-reason-input"
                  value={rejectReason}
                  onChange={handleRejectReasonChange}
                  rows={5}
                  placeholder={t("user.reject.confirm.reason.placeholder")}
                />
                {rejectReasonError && (
                  <p className="reject-reason-error">{t("user.reject.confirm.reason.required")}</p>
                )}
              </div>
              <div className="button-group">
                <button
                  type="button"
                  onClick={handleConfirmReject}
                  className="button-default-publish full-width"
                >
                  <span className="button-text">{t("user.reject.confirm.button")}</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <h1>{publishRejectObject[event][viewState].title}</h1>
              <p>{publishRejectObject[event][viewState].text}</p>
              <div className="button-group">
                <button
                  type="button"
                  onClick={handleRedirectToHome}
                  className="button-default-publish"
                >
                  <span className="button-text">{t("user.reject.go.home")}</span>
                </button>
                {!publishRejectContentError && event === URL_NAME_PUBLISH && (
                  <button
                    type="button"
                    onClick={handleRedirectToPlayer}
                    className="button-default-invert-publish"
                  >
                    <span className="button-text">{t("user.reject.go.content")}</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WithAuthorization(PublishRejectComponent);
